import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import DashboardSidebar from "../components/DashboardSidebar";
import DashboardHeader from "../components/DashboardHeader";
import MayaLogo from "../components/MayaLogo";
import {
  Search,
  Check,
  ChevronRight,
  Loader2,
  Sparkles,
  GraduationCap,
  Layers,
  Gauge,
  ArrowRight,
  Play,
  Trash2,
  Clock,
  Trophy,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DIFFICULTIES = [
  {
    key: "foundation",
    title: "Foundation",
    desc: "Building confidence. Clear, structured questions to get you started.",
  },
  {
    key: "standard",
    title: "Standard",
    desc: "Realistic MMI-level. Mirrors what most UK schools ask on the day.",
  },
  {
    key: "challenging",
    title: "Challenging",
    desc: "For top-ranked schools with tough and layered questions.",
  },
];

const STATION_COUNTS = [1, 4, 8, 10];

/* ---------- Small building-blocks ---------- */
const StepChip = ({ n, title, active, done }) => (
  <div
    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[12.5px] font-medium border transition ${
      done
        ? "bg-[#e6f7ee] text-[#15803d] border-[#c1e9d0]"
        : active
        ? "bg-[#1d4ed8] text-white border-[#1d4ed8]"
        : "bg-white text-slate-500 border-slate-200"
    }`}
  >
    <span
      className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] ${
        done ? "bg-[#15803d] text-white" : active ? "bg-white/25 text-white" : "bg-slate-100 text-slate-500"
      }`}
    >
      {done ? <Check className="w-3 h-3" /> : n}
    </span>
    {title}
  </div>
);

const SelectableCard = ({ selected, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full text-left rounded-2xl border p-4 transition ${
      selected
        ? "bg-[#eef4ff] border-[#1d4ed8] shadow-[0_10px_30px_-15px_rgba(29,78,216,0.4)]"
        : "bg-white border-slate-200 hover:border-[#1d4ed8]/50 hover:shadow-sm"
    }`}
  >
    {children}
  </button>
);

/* ---------- Maya Home ---------- */
const MayaHome = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState({ universities: [], themes: [] });
  const [loading, setLoading] = useState(true);
  const [univQuery, setUnivQuery] = useState("");
  const [themeQuery, setThemeQuery] = useState("");
  const [selectedUniv, setSelectedUniv] = useState("");
  const [selectedThemes, setSelectedThemes] = useState([]); // theme titles
  const [count, setCount] = useState(6);
  const [difficulty, setDifficulty] = useState("standard");
  const [starting, setStarting] = useState(false);
  const [history, setHistory] = useState([]);
  const [usage, setUsage] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [c, h, u, billing] = await Promise.all([
          axios.get(`${API}/maya/config`),
          axios.get(`${API}/maya/interviews`),
          axios.get(`${API}/me/station-usage`).catch(() => axios.get(`${API}/maya/usage`)),
          axios.get(`${API}/me/billing`).catch(() => ({ data: null })),
        ]);
        if (!mounted) return;
        setConfig(c.data);
        setHistory(h.data.interviews || []);
        // Prefer /me/station-usage (billing-cycle aware). Normalise shape.
        const uData = u.data || {};
        setUsage({
          ...uData,
          can_start: (uData.used ?? 0) < (uData.limit ?? 15),
          resets_at: uData.resets_at || billing.data?.next_reset_at || null,
        });
      } catch (e) {
        setError("Could not load Maya configuration.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const filteredUniv = useMemo(
    () =>
      (config.universities || []).filter((u) =>
        u.toLowerCase().includes(univQuery.trim().toLowerCase())
      ),
    [config.universities, univQuery]
  );

  const filteredThemes = useMemo(
    () =>
      (config.themes || []).filter((t) =>
        t.title.toLowerCase().includes(themeQuery.trim().toLowerCase()) ||
        t.guide.toLowerCase().includes(themeQuery.trim().toLowerCase())
      ),
    [config.themes, themeQuery]
  );

  const toggleTheme = (title) => {
    setSelectedThemes((prev) =>
      prev.includes(title) ? prev.filter((t) => t !== title) : [...prev, title]
    );
  };

  const canStart = selectedUniv && selectedThemes.length > 0 && count && difficulty && !starting && (usage?.can_start !== false);
  const step = !selectedUniv ? 1 : selectedThemes.length === 0 ? 2 : 3;

  const startInterview = async () => {
    setStarting(true);
    setError("");
    try {
      const res = await axios.post(`${API}/maya/interviews`, {
        university: selectedUniv,
        themes: selectedThemes,
        station_count: count,
        difficulty,
      });
      // Backend prepares questions in the background — head straight to the
      // interview page which polls until preparing:false.
      navigate(`/maya/interview/${res.data.id}`);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not start interview.");
      setStarting(false);
    }
  };

  const deleteInterview = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this interview?")) return;
    try {
      await axios.delete(`${API}/maya/interviews/${id}`);
      setHistory((h) => h.filter((x) => x.id !== id));
    } catch (_) {}
  };

  return (
    <div className="min-h-screen bg-[#f5f8fd] flex">
      <DashboardSidebar />
      <div className="flex-1 min-w-0">
        <DashboardHeader />
        <main className="p-6 lg:p-10 max-w-[1200px] mx-auto">
          {/* Hero */}
          <section className="relative overflow-hidden rounded-[28px] bg-gradient-to-br from-[#0b2a6b] via-[#1d4ed8] to-[#3b6ff5] text-white p-8 md:p-14 shadow-[0_30px_80px_-25px_rgba(29,78,216,0.55)]">
            <div className="absolute -top-24 -right-24 w-[420px] h-[420px] bg-white/10 rounded-full blur-3xl" />
            <div className="absolute -bottom-24 -left-24 w-[360px] h-[360px] bg-white/10 rounded-full blur-3xl" />
            <div className="relative flex flex-col items-center text-center">
              <MayaLogo variant="on-blue" className="h-16 md:h-20 drop-shadow-lg" />
              <p className="mt-4 text-white/85 text-[16px] md:text-[18px] font-medium tracking-wide">
                Your AI MMI Interview Coach
              </p>
              <p className="mt-4 max-w-xl text-white/75 text-[14.5px] leading-relaxed">
                Configure a mock interview tailored to your target medical school. Maya adapts every station to the standard that university actually expects.
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <StepChip n={1} title="University" active={step === 1} done={!!selectedUniv} />
                <StepChip n={2} title="Themes" active={step === 2} done={selectedThemes.length > 0} />
                <StepChip n={3} title="Format" active={step === 3} done={false} />
              </div>
            </div>
          </section>

          {loading && (
            <div className="flex items-center justify-center py-16 text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Setting up Maya…
            </div>
          )}

          {!loading && (
            <div className="mt-10 grid gap-6">
              {/* Monthly usage */}
              {usage && (
                <section className="rounded-3xl bg-white border border-slate-100 shadow-sm p-6 md:p-8">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-[#1d4ed8]">Monthly usage</p>
                      <p className="mt-1 text-[18px] font-semibold text-slate-900">
                        {usage.used}/{usage.limit} stations used this month
                      </p>
                      <p className="text-[12.5px] text-slate-500 mt-0.5">
                        {usage.can_start
                          ? `${usage.remaining} station${usage.remaining === 1 ? "" : "s"} remaining. ${
                              usage.resets_at
                                ? `Resets ${new Date(usage.resets_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}.`
                                : "Resets on your next billing date."
                            }`
                          : "You've reached this month's limit. Your previous interviews and feedback stay accessible."}
                      </p>
                    </div>
                    <div className="min-w-[220px]">
                      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${
                            usage.used >= usage.limit ? "bg-red-500" : usage.used / usage.limit > 0.66 ? "bg-amber-500" : "bg-[#1d4ed8]"
                          }`}
                          style={{ width: `${Math.min(100, (usage.used / usage.limit) * 100)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </section>
              )}

              {/* Section 1 — University */}
              <section className="rounded-3xl bg-white border border-slate-100 shadow-sm p-6 md:p-8">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#eef4ff] text-[#1d4ed8] flex items-center justify-center">
                      <GraduationCap className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-[#1d4ed8]">Step 1</p>
                      <h2 className="text-[20px] font-semibold text-slate-900">Choose your target university</h2>
                    </div>
                  </div>
                  <div className="relative w-full max-w-xs">
                    <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      value={univQuery}
                      onChange={(e) => setUnivQuery(e.target.value)}
                      placeholder="Search medical schools…"
                      className="w-full pl-10 pr-4 py-2.5 rounded-full border border-slate-200 text-sm focus:outline-none focus:border-[#1d4ed8] focus:ring-2 focus:ring-[#1d4ed8]/10"
                    />
                  </div>
                </div>
                <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
                  {filteredUniv.map((u) => {
                    const sel = selectedUniv === u;
                    return (
                      <button
                        key={u}
                        type="button"
                        onClick={() => setSelectedUniv(u)}
                        className={`px-3.5 py-2.5 rounded-xl text-[12.5px] font-medium border text-left leading-tight transition ${
                          sel
                            ? "bg-[#1d4ed8] text-white border-[#1d4ed8] shadow-sm"
                            : "bg-white text-slate-700 border-slate-200 hover:border-[#1d4ed8] hover:text-[#1d4ed8]"
                        }`}
                      >
                        {u}
                      </button>
                    );
                  })}
                  {filteredUniv.length === 0 && (
                    <p className="col-span-full text-center text-slate-400 text-sm py-6">No matches.</p>
                  )}
                </div>
              </section>

              {/* Section 2 — Themes */}
              <section className="rounded-3xl bg-white border border-slate-100 shadow-sm p-6 md:p-8">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#eef4ff] text-[#1d4ed8] flex items-center justify-center">
                      <Layers className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-[#1d4ed8]">Step 2</p>
                      <h2 className="text-[20px] font-semibold text-slate-900">
                        Interview themes{" "}
                        <span className="text-slate-400 text-[13.5px] font-medium">
                          ({selectedThemes.length} selected)
                        </span>
                      </h2>
                    </div>
                  </div>
                  <div className="relative w-full max-w-xs">
                    <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      value={themeQuery}
                      onChange={(e) => setThemeQuery(e.target.value)}
                      placeholder="Search themes…"
                      className="w-full pl-10 pr-4 py-2.5 rounded-full border border-slate-200 text-sm focus:outline-none focus:border-[#1d4ed8] focus:ring-2 focus:ring-[#1d4ed8]/10"
                    />
                  </div>
                </div>
                <div className="mt-5 max-h-[360px] overflow-y-auto pr-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {filteredThemes.map((t) => {
                    const sel = selectedThemes.includes(t.title);
                    return (
                      <button
                        key={`${t.guide}/${t.slug}`}
                        type="button"
                        onClick={() => toggleTheme(t.title)}
                        className={`text-left px-3.5 py-2.5 rounded-xl border transition group ${
                          sel
                            ? "bg-[#eef4ff] border-[#1d4ed8]"
                            : "bg-white border-slate-200 hover:border-[#1d4ed8]/50"
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          <span
                            className={`mt-0.5 w-4 h-4 rounded-md border shrink-0 flex items-center justify-center ${
                              sel ? "bg-[#1d4ed8] border-[#1d4ed8]" : "border-slate-300"
                            }`}
                          >
                            {sel && <Check className="w-3 h-3 text-white" />}
                          </span>
                          <div>
                            <p className="text-[13.5px] font-medium text-slate-900 leading-tight">{t.title}</p>
                            <p className="text-[11px] text-slate-500 mt-0.5">{t.guide}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                  {filteredThemes.length === 0 && (
                    <p className="col-span-full text-center text-slate-400 text-sm py-6">No matches.</p>
                  )}
                </div>
              </section>

              {/* Section 3 — Format */}
              <section className="rounded-3xl bg-white border border-slate-100 shadow-sm p-6 md:p-8 grid md:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#eef4ff] text-[#1d4ed8] flex items-center justify-center">
                      <Sparkles className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-[#1d4ed8]">Step 3</p>
                      <h2 className="text-[20px] font-semibold text-slate-900">How many stations?</h2>
                    </div>
                  </div>
                  <div className="mt-5 grid grid-cols-4 gap-2">
                    {STATION_COUNTS.map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setCount(n)}
                        className={`py-3 rounded-xl border text-sm font-semibold transition ${
                          count === n
                            ? "bg-[#1d4ed8] text-white border-[#1d4ed8]"
                            : "bg-white text-slate-700 border-slate-200 hover:border-[#1d4ed8]"
                        }`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                  <p className="mt-3 text-[12px] text-slate-500">
                    Each station is a new question, evaluated independently. Most UK schools run 6–10.
                  </p>
                </div>
                <div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#eef4ff] text-[#1d4ed8] flex items-center justify-center">
                      <Gauge className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-[#1d4ed8]">Step 3</p>
                      <h2 className="text-[20px] font-semibold text-slate-900">Difficulty</h2>
                    </div>
                  </div>
                  <div className="mt-5 space-y-2">
                    {DIFFICULTIES.map((d) => (
                      <SelectableCard
                        key={d.key}
                        selected={difficulty === d.key}
                        onClick={() => setDifficulty(d.key)}
                      >
                        <div className="flex items-start gap-3">
                          <span
                            className={`mt-1 w-4 h-4 rounded-full border shrink-0 flex items-center justify-center ${
                              difficulty === d.key ? "border-[#1d4ed8] bg-[#1d4ed8]" : "border-slate-300"
                            }`}
                          >
                            {difficulty === d.key && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                          </span>
                          <div>
                            <p className="font-semibold text-slate-900 text-[14.5px]">{d.title}</p>
                            <p className="text-[12.5px] text-slate-500 leading-relaxed">{d.desc}</p>
                          </div>
                        </div>
                      </SelectableCard>
                    ))}
                  </div>
                </div>
              </section>

              {/* Sticky launch bar */}
              <div className="rounded-3xl bg-gradient-to-r from-[#0b2a6b] to-[#1d4ed8] text-white p-6 md:p-7 shadow-[0_20px_60px_-25px_rgba(29,78,216,0.6)] flex items-center justify-between flex-wrap gap-4">
                <div>
                  <div className="flex items-center gap-2 text-[12px] text-white/70 uppercase tracking-wider font-medium">
                    <MayaLogo variant="on-blue" className="h-4" />
                    <span>is ready when you are</span>
                  </div>
                  <p className="mt-1 text-[17px] font-semibold">
                    {selectedUniv || "Pick a university"} · {selectedThemes.length || 0} themes · {count} stations · <span className="capitalize">{difficulty}</span>
                  </p>
                  {error && <p className="mt-1 text-[13px] text-red-200">{error}</p>}
                </div>
                <button
                  onClick={startInterview}
                  disabled={!canStart}
                  className="px-7 py-3.5 rounded-full bg-white text-[#1d4ed8] font-semibold hover:bg-slate-100 transition inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                >
                  {starting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Preparing your stations…
                    </>
                  ) : usage && !usage.can_start ? (
                    <>Monthly limit reached</>
                  ) : (
                    <>Start Interview <ArrowRight className="w-4 h-4" /></>
                  )}
                </button>
              </div>

              {/* Previous interviews */}
              <section className="rounded-3xl bg-white border border-slate-100 shadow-sm p-6 md:p-8">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-[20px] font-semibold text-slate-900">Previous Interviews</h2>
                  {history.length > 0 && (
                    <span className="text-[12.5px] text-slate-500">{history.length} total</span>
                  )}
                </div>
                {history.length === 0 ? (
                  <div className="text-center py-10">
                    <div className="w-16 h-16 rounded-2xl bg-[#eef4ff] text-[#1d4ed8] flex items-center justify-center mx-auto">
                      <MayaLogo variant="on-white" className="h-6" />
                    </div>
                    <p className="mt-3 font-semibold text-slate-900">No interviews yet</p>
                    <p className="text-[13px] text-slate-500 mt-1">Configure and start your first Maya interview above.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {history.map((h) => (
                      <button
                        key={h.id}
                        onClick={() => navigate(`/maya/interview/${h.id}`)}
                        className="text-left rounded-2xl border border-slate-100 bg-white hover:border-[#1d4ed8]/40 hover:shadow-md p-4 transition group"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className={`text-[10.5px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                            h.completed ? "bg-[#e6f7ee] text-[#15803d]" : "bg-[#eef4ff] text-[#1d4ed8]"
                          }`}>
                            {h.completed ? "Completed" : "In progress"}
                          </span>
                          <span className="text-[11px] text-slate-400 inline-flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {new Date(h.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="font-semibold text-slate-900">{h.university}</p>
                        <p className="text-[12.5px] text-slate-500 mt-0.5 line-clamp-1">
                          {h.themes.join(" · ")}
                        </p>
                        <div className="mt-3 flex items-center justify-between text-[12.5px]">
                          <span className="text-slate-500">
                            {h.stations_done}/{h.station_count} stations · <span className="capitalize">{h.difficulty}</span>
                          </span>
                          <span className="flex items-center gap-2">
                            {h.average_score != null && (
                              <span className="inline-flex items-center gap-1 text-[#15803d] font-semibold">
                                <Trophy className="w-3.5 h-3.5" /> {h.average_score}/10
                              </span>
                            )}
                            <span className="text-[#1d4ed8] font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                              {h.completed ? "View feedback" : "Continue"} <ChevronRight className="w-3.5 h-3.5" />
                            </span>
                          </span>
                        </div>
                        <button
                          onClick={(e) => deleteInterview(h.id, e)}
                          className="mt-3 text-[11px] text-slate-400 hover:text-red-500 inline-flex items-center gap-1"
                        >
                          <Trash2 className="w-3 h-3" /> Delete
                        </button>
                      </button>
                    ))}
                  </div>
                )}
              </section>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default MayaHome;
